export type CanonicalPeriod = {
  period: string
  year: number
  month: number
  period_type?: string
  [k: string]: any
}

export function collectForecastPeriods(payload: any): CanonicalPeriod[] {
  if (!payload || typeof payload !== 'object') return []

  const productForecasts = (payload?.product?.prices ?? []).filter((price: any) => price.period_type === `forecast`)

  const productPeriods = productForecasts.map((price: any) => {
    const year = price.fiscal_year ?? Number(price.period?.split(`-`)[0])
    const month = price.fiscal_month ?? Number(price.period?.split(`-`)[1])

    return { period: price.period, year: Number(year), month: Number(month) }
  })

  const orderProductForecasts = payload?.orderProduct?.orders?.filter((order: any) => order.period_type === `forecast`) ?? []
  const orderProductPeriods = orderProductForecasts.map((order: any) => ({
    period: order.period,
    year: order.fiscal_year,
    month: order.fiscal_month
  }))

  const orderDataForecasts = payload?.orderDataPeriods?.filter((period: any) => period.period_type === `forecast`) ?? []
  const orderDataPeriods = orderDataForecasts.map((period: any) => ({ period: period.period, year: period.fiscal_year, month: period.fiscal_month }))

  const effectivePeriodType = payload?.queryPeriod === 'all' ? 'forecast' : (payload?.queryPeriod ?? 'forecast')
  const marketingFunnelForecasts = payload?.marketingFunnel?.data?.filter((row: any) => (row.type ?? row.period_type) === effectivePeriodType) ?? []
  const marketingFunnelPeriods = marketingFunnelForecasts.map((row: any) => ({ period: row.period, year: row.fiscal_year, month: row.fiscal_month }))

  const organicFunnelForecasts = payload?.organicFunnel?.data?.filter((row: any) => (row.type ?? row.period_type) === effectivePeriodType) ?? []
  const organicFunnelPeriods = organicFunnelForecasts.map((row: any) => ({ period: row.period, year: row.fiscal_year, month: row.fiscal_month }))

  const salesFunnelForecasts = payload?.salesFunnel?.data?.filter((row: any) => (row.type ?? row.period_type) === effectivePeriodType) ?? []
  const salesFunnelPeriods = salesFunnelForecasts.map((row: any) => ({ period: row.period, year: row.fiscal_year, month: row.fiscal_month }))

  const segmentForecasts = payload?.customerSegmentPeriods?.filter((period: any) => period.period_type === `forecast`) ?? []
  const segmentPeriods = segmentForecasts.map((period: any) => ({ period: period.period, year: period.fiscal_year, month: period.fiscal_month }))

  const directCostForecasts = payload?.directCostPeriods?.filter((period: any) => period.period_type === `forecast`) ?? []
  const directCostPeriods = directCostForecasts.map((period: any) => ({
    period: period.period,
    year: period.fiscal_year,
    month: period.fiscal_month
  }))

  const directLabourForecasts = payload?.directLabourPeriods?.filter((period: any) => period.period_type === `forecast`) ?? []
  const directLabourPeriods = directLabourForecasts.map((period: any) => ({
    period: period.period,
    year: period.fiscal_year,
    month: period.fiscal_month
  }))

  const personnelForecasts = payload?.personnelPeriods?.filter((period: any) => period.period_type === `forecast`) ?? []
  const personnelPeriods = personnelForecasts.map((period: any) => ({ period: period.period, year: period.fiscal_year, month: period.fiscal_month }))

  const opexForecasts =
    payload?.opexPeriods?.filter((period: any) => {
      const effective = payload?.queryPeriod === 'all' ? 'forecast' : (payload?.queryPeriod ?? 'forecast')
      return period.period_type === effective
    }) ?? []
  const opexPeriods = opexForecasts.map((period: any) => ({ period: period.period, year: period.fiscal_year, month: period.fiscal_month }))

  const oicForecasts = payload?.oicPeriods?.filter((period: any) => period.type === `forecast`) ?? []
  const oicPeriods = oicForecasts.map((period: any) => ({
    period: period.period,
    year: period.fiscal_year,
    month: period.fiscal_month ?? Number(period.period?.split(`-`)[1])
  }))

  const receivableForecasts = payload?.receivablePeriods?.filter((period: any) => period.period_type === (payload?.queryPeriod ?? `forecast`)) ?? []
  const receivablePeriods = receivableForecasts.map((period: any) => ({
    period: period.period,
    year: period.fiscal_year,
    month: period.fiscal_month
  }))

  const inventoryForecasts = payload?.inventoryPeriods?.filter((period: any) => period.period_type === (payload?.queryPeriod ?? `forecast`)) ?? []
  const inventoryPeriods = inventoryForecasts.map((period: any) => ({ period: period.period, year: period.fiscal_year, month: period.fiscal_month }))

  const payableForecasts = payload?.payablePeriods?.filter((period: any) => period.period_type === (payload?.queryPeriod ?? `forecast`)) ?? []
  const payablePeriods = payableForecasts.map((period: any) => ({ period: period.period, year: period.fiscal_year, month: period.fiscal_month }))

  const provisionForecasts = payload?.provisionPeriods?.filter((period: any) => period.period_type === (payload?.queryPeriod ?? `forecast`)) ?? []
  const provisionPeriods = provisionForecasts.map((period: any) => ({ period: period.period, year: period.fiscal_year, month: period.fiscal_month }))

  const equityForecasts = payload?.equityPeriods?.filter((period: any) => period.period_type === (payload?.queryPeriod ?? `forecast`)) ?? []
  const equityPeriods = equityForecasts.map((period: any) => ({ period: period.period, year: period.fiscal_year, month: period.fiscal_month }))

  const grantForecasts = payload?.grantPeriods?.filter((period: any) => period.period_type === (payload?.queryPeriod ?? `forecast`)) ?? []
  const grantPeriods = grantForecasts.map((period: any) => ({ period: period.period, year: period.fiscal_year, month: period.fiscal_month }))

  const explicitlyCollected: { period: string; year: number; month: number }[] = [
    ...productPeriods,
    ...segmentPeriods,
    ...orderProductPeriods,
    ...orderDataPeriods,
    ...directCostPeriods,
    ...directLabourPeriods,
    ...personnelPeriods,
    ...oicPeriods,
    ...opexPeriods,
    ...receivablePeriods,
    ...inventoryPeriods,
    ...payablePeriods,
    ...provisionPeriods,
    ...equityPeriods,
    ...grantPeriods,
    ...marketingFunnelPeriods,
    ...organicFunnelPeriods,
    ...salesFunnelPeriods
  ]

  const otherPeriods: Array<{ period: string; year: number; month: number }> = []

  try {
    const p = payload as any

    if (p && typeof p === 'object') {
      Object.keys(p).forEach(key => {
        const val = p[key]

        if (key === 'historicalValues') return

        if (!Array.isArray(val)) return

        val.forEach((item: any) => {
          if (!item || typeof item !== 'object') return

          if (typeof item.period !== 'string') return

          const year = item.fiscal_year ?? item.year ?? Number(item.period.split('-')[0])
          const month = item.fiscal_month ?? item.month ?? Number(item.period.split('-')[1])

          if (!Number.isFinite(year) || !Number.isFinite(month)) return

          // console.info('has Other Periods', { item, year, month, key })

          otherPeriods.push({ period: item.period, year: Number(year), month: Number(month) })
        })
      })
    }
  } catch (error) {
    console.error('Error collecting other periods:', error)
  }

  const map = new Map<string, { period: string; year: number; month: number }>()
  ;[...explicitlyCollected, ...otherPeriods].forEach(p => {
    if (!p || !p.period) return

    if (!map.has(p.period)) map.set(p.period, p)
  })

  return Array.from(map.values()).sort((a, b) => a.year - b.year || a.month - b.month)
}
